import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Flame, ArrowUpRight, BookOpen, MessageCircle, Zap,
  Bookmark, Target, Coins, Sparkles, ChevronRight, Clock, AlertCircle,
  Play, FileText, Lock, LifeBuoy, Mail, Phone, ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../contexts/AuthContext";
import ChallengeHub from "../components/ChallengeHub";
import StreakSnapshotWidget from "../components/StreakSnapshotWidget";
import { fmt12h, fmt12hRange } from "../utils/timeFormat";
import "../lms.css";
import "../portal.css";

const QURAN_VERSES = [
  { text: "And say: 'My Lord, increase me in knowledge.'", src: "Qur'an 20:114" },
  { text: "Whoever treads a path seeking knowledge, Allah makes the path to Paradise easy.", src: "Hadith" },
  { text: "Indeed, with hardship [will be] ease.", src: "Qur'an 94:6" },
];

/** 12 moody greetings that rotate by `dayOfYear + hourBand` so the same student
 * sees the SAME greeting within a 4-hour window but variety across the week. */
const GREETING_LINES = [
  "The morning is patient",
  "One more compounding rep",
  "Quiet focus beats loud effort",
  "Small ticks, big tide",
  "Deep breath, sharper mind",
  "Today you're the librarian of your future",
  "Close the loop once more",
  "Consistency is a gentle verb",
  "Ten pages now, ten pages of peace later",
  "The best moves are boring",
  "You are what you revise",
  "Return to the rep",
];

/** Iter 190 — Client-side live window computation. Backend
 *  `is_in_window` / `mins_until_open` go stale within seconds of the
 *  fetch. This helper reads the student's stored `start_min` / `end_min`
 *  against the current wall clock in their timezone. */
function _tzNowMinutes(tzName) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tzName || "UTC", hour12: false, hour: "2-digit", minute: "2-digit",
    }).formatToParts(new Date()).reduce((acc, p) => {
      if (p.type !== "literal") acc[p.type] = Number(p.value);
      return acc;
    }, {});
    const h = parts.hour === 24 ? 0 : parts.hour;
    return h * 60 + parts.minute;
  } catch {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
}
function computeLiveWindow(w) {
  if (!w) return { inWindow: false, minsUntilOpen: null };
  const now = _tzNowMinutes(w.timezone);
  const s = Number(w.start_min || 0), e = Number(w.end_min || 0);
  const wraps = e < s;
  const inWindow = wraps ? (now >= s || now <= e) : (now >= s && now <= e);
  let minsUntilOpen = null;
  if (!inWindow) {
    if (wraps) minsUntilOpen = s - now;
    else minsUntilOpen = now < s ? s - now : (1440 - now) + s;
  }
  return { inWindow, minsUntilOpen: minsUntilOpen != null ? Math.max(0, minsUntilOpen) : null };
}

function pickGreeting(name) {
  const d = new Date();
  const doy = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 864e5);
  const hourBand = Math.floor(d.getHours() / 4);
  const idx = (doy * 7 + hourBand) % GREETING_LINES.length;
  return `${GREETING_LINES[idx]}, ${name}.`;
}

/** Weather-aware hero gradient — 3 presets by device clock.
 *   00-06 → Night  (deep indigo/navy)
 *   06-11 → Dawn   (warm coral)
 *   11-17 → Day    (violet→magenta — default)
 *   17-21 → Dusk   (cool violet)
 *   21-24 → Night
 */
function heroPaletteForHour(h) {
  if (h < 6) return {
    label: "Night",
    base: "linear-gradient(135deg, #0b1033 0%, #1a1a4a 55%, #2a1a5b 100%)",
    glowA: "rgba(99, 102, 241, 0.38)",  // indigo
    glowB: "rgba(30, 58, 138, 0.32)",   // navy
    kickerBg: "rgba(99, 102, 241, 0.2)",
    kickerFg: "#c7d2fe",
    kickerBorder: "rgba(99, 102, 241, 0.4)",
    btnGrad: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    btnShadow: "0 10px 28px -8px rgba(99, 102, 241, 0.55)",
  };
  if (h < 11) return {
    label: "Dawn",
    base: "linear-gradient(135deg, #3a1a1a 0%, #5a2a2a 50%, #7a3a2a 100%)",
    glowA: "rgba(251, 146, 60, 0.40)",  // orange
    glowB: "rgba(244, 114, 182, 0.30)", // pink-coral
    kickerBg: "rgba(251, 146, 60, 0.20)",
    kickerFg: "#fed7aa",
    kickerBorder: "rgba(251, 146, 60, 0.38)",
    btnGrad: "linear-gradient(135deg, #fb923c 0%, #f472b6 100%)",
    btnShadow: "0 10px 28px -8px rgba(251, 146, 60, 0.55)",
  };
  if (h < 17) return {
    label: "Day",
    base: "linear-gradient(135deg, #1a1330 0%, #261a44 50%, #3b1f5b 100%)",
    glowA: "rgba(236, 72, 153, 0.38)",
    glowB: "rgba(124, 58, 237, 0.28)",
    kickerBg: "rgba(236, 72, 153, 0.18)",
    kickerFg: "#fbcfe8",
    kickerBorder: "rgba(236, 72, 153, 0.32)",
    btnGrad: "linear-gradient(135deg, #ec4899 0%, #a855f7 100%)",
    btnShadow: "0 10px 28px -8px rgba(236, 72, 153, 0.55)",
  };
  if (h < 21) return {
    label: "Dusk",
    base: "linear-gradient(135deg, #1e1b4b 0%, #3b1d5b 55%, #4c1d95 100%)",
    glowA: "rgba(139, 92, 246, 0.40)",
    glowB: "rgba(59, 130, 246, 0.22)",
    kickerBg: "rgba(139, 92, 246, 0.22)",
    kickerFg: "#ddd6fe",
    kickerBorder: "rgba(139, 92, 246, 0.42)",
    btnGrad: "linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)",
    btnShadow: "0 10px 28px -8px rgba(139, 92, 246, 0.58)",
  };
  return {
    label: "Night",
    base: "linear-gradient(135deg, #0b1033 0%, #1a1a4a 55%, #2a1a5b 100%)",
    glowA: "rgba(99, 102, 241, 0.38)",
    glowB: "rgba(30, 58, 138, 0.32)",
    kickerBg: "rgba(99, 102, 241, 0.2)",
    kickerFg: "#c7d2fe",
    kickerBorder: "rgba(99, 102, 241, 0.4)",
    btnGrad: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    btnShadow: "0 10px 28px -8px rgba(99, 102, 241, 0.55)",
  };
}

/** Micro-haptic buzz on supported devices (mobile). Silent no-op elsewhere. */
function haptic(ms = 20) {
  try {
    if (typeof window !== "undefined" && window.navigator?.vibrate) {
      window.navigator.vibrate(ms);
    }
  } catch (_) { /* noop */ }
}

/**
 * StudentHome v3 — premium layered dashboard.
 * Hierarchy: Greeting → Primary action → KPI row → Jump in + Cohorts → Verse.
 * Every surface floats (soft glassmorphism). Primary CTA is the loudest,
 * secondary sections sit back. Motion is subtle — entries stagger in with
 * a spring so it feels continuous, never flashy.
 */

const ENTER = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.06, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function StudentHome() {
  const { user } = useAuth();
  const [batches, setBatches] = useState([]);
  const [sv2, setSv2] = useState(null);
  const [dayStatus, setDayStatus] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 193.6 — dashboard-level batch restriction banner data.
  const [batchRestrictions, setBatchRestrictions] = useState({ restrictions: [], help_desk: null });
  const verse = QURAN_VERSES[new Date().getDate() % QURAN_VERSES.length];

  // Iter 190 — 15-second tick so `liveWinState` recomputes and the
  // "Window open / opens in Xm" strip flips instantly at boundaries.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) setTick(t => t + 1); }, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const load = () => Promise.all([
      axios.get(`${API}/me/batches`).catch(() => ({ data: [] })),
      axios.get(`${API}/me/streak-v2/window`).catch(() => ({ data: null })),
      axios.get(`${API}/me/streak-v2/today`).catch(() => ({ data: null })),
      axios.get(`${API}/me/continue`).catch(() => ({ data: { item: null } })),
      axios.get(`${API}/me/batch-restrictions`).catch(() => ({ data: { restrictions: [], help_desk: null } })),
    ]).then(([b, s, t, c, r]) => {
      setBatches(Array.isArray(b.data) ? b.data : []);
      setSv2(s.data);
      setDayStatus(t.data);
      setResume(c.data?.item || null);
      setBatchRestrictions(r.data || { restrictions: [], help_desk: null });
    }).finally(() => setLoading(false));
    load();
    const id = setInterval(() => { if (document.hidden) return; load(); }, 180000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 5)  return "Quiet hours";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Night owl";
  })();

  const firstName = (user?.name || "Logician").split(" ")[0];
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  // Iter 190 — override stale backend fields with live-computed state
  // so the "Window open / opens in Xm" strip flips at boundaries.
  const _liveWin = computeLiveWindow(dayStatus?.window);
  const isInWindow = _liveWin.inWindow;
  const minsUntilOpen = _liveWin.minsUntilOpen;
  const heroPalette = heroPaletteForHour(new Date().getHours());
  const greetingLine = pickGreeting(firstName);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-14 min-w-0" data-testid="student-hero">
      {/* ────────── 1) GREETING — the entry point ────────── */}
      <motion.div initial="hidden" animate="show" variants={ENTER} custom={0} className="mb-10">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em]"
             style={{ color: "var(--px-ink-mute)", fontFamily: "var(--font-mono)" }}>
          {today}
        </div>
        <h1 className="text-[40px] md:text-[56px] font-semibold tracking-tight leading-[1.02] mt-2"
            style={{ fontFamily: "var(--font-display)", color: "var(--px-ink)" }}>
          {greet},<br className="hidden md:block" />
          <span style={{ color: "var(--px-ink-faint)" }}> {firstName}.</span>
        </h1>
        <p className="text-[15px] mt-4 max-w-lg leading-relaxed italic"
           style={{ color: "var(--px-ink-mute)" }}>
          {greetingLine}
        </p>
      </motion.div>

      {/* Iter 193.6 — Batch restriction banner. Shows when the student has
          one or more batches restricted by an admin. Includes a Help Desk
          reveal so students immediately see how to contact support. */}
      {batchRestrictions.restrictions.length > 0 && (
        <BatchRestrictionBanner
          restrictions={batchRestrictions.restrictions}
          helpDesk={batchRestrictions.help_desk}
        />
      )}

      {/* Iter 163 · Phase 4 — Free-Batch Referral Challenge hub. Hides
          itself gracefully for non-free-batch students. */}
      <ChallengeHub />

      {/* Iter 186 — Compact streak snapshot ONLY on home. The full
          Racing Track + task list + tokens/grace live on /student/streak.
          Home stays uncluttered — the widget is a single tile that
          leads students to the full experience. */}
      <StreakSnapshotWidget />

      {/* ────────── 2) PRIMARY ACTION — the one loud thing ────────── */}
      <motion.div initial="hidden" animate="show" variants={ENTER} custom={1}
        className="mb-6 rounded-[28px] relative overflow-hidden press-btn"
        style={{
          background: heroPalette.base,
          boxShadow:
            `0 24px 60px -20px ${heroPalette.glowA},` +
            "0 8px 28px -10px rgba(15, 23, 42, 0.35)",
        }}
        data-testid="student-continue-learning"
        data-hero-palette={heroPalette.label.toLowerCase()}
      >
        {/* Glow halo */}
        <div aria-hidden className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full pointer-events-none"
             style={{ background: `radial-gradient(circle, ${heroPalette.glowA} 0%, transparent 65%)` }} />
        <div aria-hidden className="absolute -bottom-32 -left-16 w-[320px] h-[320px] rounded-full pointer-events-none"
             style={{ background: `radial-gradient(circle, ${heroPalette.glowB} 0%, transparent 65%)` }} />
        <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

        <div className="relative p-8 md:p-10 grid md:grid-cols-[1fr_auto] gap-8 items-end">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-4"
                 style={{ background: heroPalette.kickerBg, color: heroPalette.kickerFg, border: `1px solid ${heroPalette.kickerBorder}` }}>
              <Target size={10} /> Continue learning · {heroPalette.label}
            </div>
            <h2 className="text-[32px] md:text-[44px] font-semibold tracking-tight leading-[1.05]"
                style={{ fontFamily: "var(--font-display)", color: "#fff" }}>
              Today's focus drill
            </h2>
            <p className="mt-4 max-w-md text-[14.5px] leading-relaxed" style={{ color: "rgba(255,255,255,0.68)" }}>
              A 15-minute personalised rep across your weakest chapters. Start strong, stay sharp.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap md:flex-nowrap">
            <Link to="/student/study"
              onClick={() => haptic(20)}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-semibold text-[14px] press-btn focus-ring transition"
              style={{
                background: heroPalette.btnGrad,
                color: "#fff",
                boxShadow: `${heroPalette.btnShadow}, inset 0 1px 0 rgba(255,255,255,0.25)`,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
              data-testid="student-hero-cta"
            >
              Start today's drill
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/student/revision"
              onClick={() => haptic(14)}
              className="inline-flex items-center gap-2 h-12 px-5 rounded-full font-semibold text-[14px] press-btn focus-ring transition"
              style={{
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.14)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
            >
              Review saved
            </Link>
          </div>
        </div>
      </motion.div>

      {/* ────────── Continue where you left off ────────── */}
      {resume && (
        <motion.div initial="hidden" animate="show" variants={ENTER} custom={1.5}
          className="mb-6"
          data-testid="student-resume-card">
          <Link
            to={resume.resume_url || "/student/study"}
            onClick={() => haptic(18)}
            className="surface-soft press-btn focus-ring p-5 flex items-center gap-4 group"
          >
            <div className="w-11 h-11 rounded-[14px] flex items-center justify-center shrink-0"
                 style={{
                   background: resume.kind === "quiz" ? "var(--px-rose-soft)" : "var(--px-info-soft)",
                   color: resume.kind === "quiz" ? "var(--px-rose)" : "var(--px-info)",
                 }}>
              {resume.kind === "quiz" ? <FileText size={17} strokeWidth={1.75} /> : <Play size={17} strokeWidth={1.75} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                   style={{ color: "var(--px-ink-mute)", fontFamily: "var(--font-mono)" }}>
                Continue where you left off
              </div>
              <div className="text-[15px] font-semibold truncate" style={{ color: "var(--px-ink)" }}>
                {resume.title}
              </div>
              {resume.subtitle && (
                <div className="text-[12px] truncate mt-0.5" style={{ color: "var(--px-ink-mute)" }}>
                  {resume.subtitle}
                </div>
              )}
            </div>
            <div className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full font-semibold text-[12.5px] transition"
                 style={{ background: "var(--px-ink)", color: "#fff" }}>
              Resume <ArrowUpRight size={13} />
            </div>
          </Link>
        </motion.div>
      )}

      {/* Check-in banners (unchanged logic) */}
      {dayStatus?.window && isInWindow && !dayStatus.has_checked_in && (
        <Link to="/student/streak"
          className="surface-soft press-btn p-5 mb-6 flex items-center justify-between gap-3 flex-wrap block"
          style={{ borderColor: "rgba(45,232,165,0.45)", background: "linear-gradient(135deg, rgba(132,204,185,0.18) 0%, rgba(255,255,255,0.7) 60%)" }}
          data-testid="dash-checkin-banner">
          <div className="flex items-center gap-3">
            <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.4, repeat: Infinity }}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "var(--px-success-soft)" }}>
              <Flame size={18} style={{ color: "var(--px-success)" }} />
            </motion.div>
            <div>
              <div className="text-[12px] font-bold uppercase tracking-[0.12em]" style={{ color: "var(--px-success)" }}>Check-in window is open</div>
              <div className="text-[13px] mt-0.5" style={{ color: "var(--px-ink)" }}>Lock in today's tasks to continue your streak.</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-[13px] font-semibold"
                style={{ background: "var(--px-success)", color: "#fff" }}>
            Check in <ArrowUpRight size={13} />
          </span>
        </Link>
      )}
      {dayStatus?.window && !isInWindow && !dayStatus.has_checked_in && typeof minsUntilOpen === "number" && minsUntilOpen <= 60 && (
        <div className="surface-soft p-4 mb-6 flex items-center gap-3" data-testid="dash-checkin-upcoming">
          <AlertCircle size={16} style={{ color: "var(--accent-deep)" }} />
          <div className="text-[13px]" style={{ color: "var(--px-ink)" }}>
            Your window ({fmt12hRange(dayStatus.window.start_time, dayStatus.window.end_time)}) opens in <strong>{minsUntilOpen}m</strong>.
          </div>
        </div>
      )}

      {/* ────────── 3) KPI STRIP ────────── */}
      <motion.div initial="hidden" animate="show" variants={ENTER} custom={2}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10"
        data-testid="student-stats">
        <KPI
          label="Streak"
          value={sv2?.state?.current_streak ?? "—"}
          unit="days"
          icon={Flame}
          tint="rose"
          hint={sv2?.state?.best_streak
            ? `best ${sv2.state.best_streak} · rolls at ${sv2?.window ? fmt12h(sv2.window.start_time) : "your check-in time"}`
            : `rolls at ${sv2?.window ? fmt12h(sv2.window.start_time) : "your check-in time"}`}
          to="/student/streak"
        />
        <KPI
          label="Today"
          value={dayStatus?.has_checked_in ? (dayStatus?.today?.closed_at ? "Closed" : "Checked in") : (isInWindow ? "Open" : "Closed")}
          icon={Clock}
          tint={dayStatus?.has_checked_in ? "sage" : isInWindow ? "sand" : "lilac"}
          hint={sv2?.window ? fmt12hRange(sv2.window.start_time, sv2.window.end_time) : "pick your window"}
          to="/student/streak"
        />
        <KPI
          label="Points"
          value={sv2?.state?.total_points ?? 0}
          icon={Target}
          tint="sage"
          hint="leaderboard score"
          to="/student/leaderboard"
        />
        <KPI
          label="Tokens"
          value={sv2?.token_info?.tokens_available ?? 0}
          icon={Coins}
          tint="sand"
          hint="protection & window changes"
          to="/student/streak"
        />
      </motion.div>

      {/* ────────── 4) JUMP IN + COHORTS ────────── */}
      <div className="grid md:grid-cols-[1.15fr_1fr] gap-5 md:gap-6 mb-10">
        {/* Jump in */}
        <motion.div initial="hidden" animate="show" variants={ENTER} custom={3} className="min-w-0">
          <SectionLabel>Jump in</SectionLabel>
          <div className="grid grid-cols-2 gap-3 md:gap-4 mt-3">
            {[
              { Icon: BookOpen,       label: "Study",      hint: "Subjects & lectures",   to: "/student/study",       tint: "sky" },
              { Icon: Bookmark,       label: "Revision",   hint: "Saved MCQs",            to: "/student/revision",    tint: "lilac" },
              { Icon: Zap,            label: "Flashcards", hint: "Spaced repetition",     to: "/student/flashcards",  tint: "sand" },
              { Icon: MessageCircle,  label: "Mentor",     hint: "Your MBBS guide",       to: "/student/mentor",      tint: "rose" },
            ].map((q) => (
              <Link key={q.label} to={q.to}
                className="surface-soft press-btn focus-ring p-5 flex flex-col gap-3 relative group"
                data-testid={`student-quick-${q.label.toLowerCase()}`}>
                <div className={`w-11 h-11 rounded-[14px] flex items-center justify-center tint-${q.tint}`}>
                  <q.Icon size={17} style={{ color: "var(--px-ink)" }} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="text-[14.5px] font-semibold tracking-tight" style={{ color: "var(--px-ink)" }}>{q.label}</div>
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--px-ink-mute)" }}>{q.hint}</div>
                </div>
                <ChevronRight size={14} className="absolute top-5 right-5 transition-all group-hover:translate-x-0.5"
                              style={{ color: "var(--px-ink-faint)" }} />
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Cohorts */}
        <motion.div initial="hidden" animate="show" variants={ENTER} custom={4} className="min-w-0">
          <SectionLabel>Your cohorts</SectionLabel>
          <div className="mt-3 min-w-0">
            {loading ? (
              <div className="portal-shimmer h-[190px] rounded-[24px]" />
            ) : batches.length === 0 ? (
              <div className="surface-soft p-8 text-center" data-testid="student-no-batches">
                <Sparkles size={18} className="mx-auto mb-3" style={{ color: "var(--px-rose)" }} />
                <div className="text-[15px] font-semibold" style={{ color: "var(--px-ink)" }}>No active batch yet</div>
                <p className="text-[12.5px] mt-1 max-w-sm mx-auto" style={{ color: "var(--px-ink-mute)" }}>
                  Your admin is finalising access — usually under 24h after payment.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {batches.slice(0, 3).map((b, i) => (
                  <div key={b.id}
                    className={`surface-soft press-btn p-4 flex items-start gap-3 min-w-0 ${i === 0 ? "tint-rose" : i === 1 ? "tint-sky" : "tint-sand"}`}
                    data-testid={`batch-card-${b.slug}`}>
                    <div className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0"
                         style={{ background: "var(--px-ink)", color: "#fff" }}>
                      <BookOpen size={16} strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      {b.is_restricted ? (
                        <span
                          className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase tracking-[0.1em] mb-1"
                          style={{ background: "#fee2e2", color: "#b91c1c" }}
                          data-testid="batch-restricted-chip"
                          title={b.restricted_reason || "Access to this batch has been restricted"}
                        >
                          🔒 Restricted
                        </span>
                      ) : (
                        <span className="inline-block text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase tracking-[0.1em] mb-1"
                              style={{ background: "var(--px-success-soft)", color: "var(--px-success)" }}>
                          Active
                        </span>
                      )}
                      <h3 className="text-[14px] font-semibold leading-tight truncate" style={{ color: "var(--px-ink)" }} title={b.name}>{b.name}</h3>
                      <div className="mt-0.5 text-[10.5px] font-mono uppercase tracking-[0.12em] truncate" style={{ color: "var(--px-ink-faint)" }}>
                        {b.enrollment?.subscription_type === "per_subject" ? "per-subject" : "all subjects"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* ────────── 5) VERSE ────────── */}
      <motion.div initial="hidden" animate="show" variants={ENTER} custom={5}
        className="surface-soft p-6 md:p-8 flex items-start gap-4 tint-sand"
        data-testid="student-verse">
        <div className="shrink-0 w-11 h-11 rounded-[14px] flex items-center justify-center"
             style={{ background: "var(--px-gold-soft)", border: "1px solid rgba(232,169,64,0.3)" }}>
          <Sparkles size={16} style={{ color: "var(--px-gold)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <SectionLabel color="gold">Today's verse</SectionLabel>
          <p className="text-[17px] md:text-[19px] mt-2 leading-snug italic font-medium"
             style={{ fontFamily: "var(--font-display)", color: "var(--px-ink)" }}>
            "{verse.text}"
          </p>
          <div className="text-[11px] font-mono mt-2 uppercase tracking-[0.14em]" style={{ color: "var(--px-ink-mute)" }}>{verse.src}</div>
        </div>
      </motion.div>
    </div>
  );
}

function SectionLabel({ children, color = "ink" }) {
  const palette = { ink: "var(--px-ink-mute)", gold: "var(--px-gold)" };
  return (
    <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em]"
         style={{ color: palette[color], fontFamily: "var(--font-mono)" }}>
      {children}
    </div>
  );
}

function KPI({ label, value, unit, icon: Icon, tint, hint, to }) {
  const body = (
    <div className={`surface-soft press-btn focus-ring h-full p-5 flex items-start gap-3 tint-${tint}`}>
      <div className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0"
           style={{ background: "rgba(255,255,255,0.6)", border: "1px solid rgba(11,11,13,0.04)" }}>
        <Icon size={15} style={{ color: "var(--px-ink)" }} strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
             style={{ color: "var(--px-ink-mute)", fontFamily: "var(--font-mono)" }}>
          {label}
        </div>
        <div className="flex items-baseline gap-1.5 mt-1">
          <span className="text-[22px] md:text-[26px] font-semibold tracking-tight leading-none"
                style={{ color: "var(--px-ink)", fontFamily: "var(--font-display)" }}>
            {value}
          </span>
          {unit && (
            <span className="text-[10.5px] font-mono uppercase tracking-[0.12em]" style={{ color: "var(--px-ink-mute)" }}>
              {unit}
            </span>
          )}
        </div>
        {hint && (
          <div className="text-[11px] mt-1 truncate" style={{ color: "var(--px-ink-faint)" }}>
            {hint}
          </div>
        )}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}


// ────────────────────────────────────────────────────────────────
// Iter 193.6 — Dashboard batch restriction banner.
// Shows a professional, non-intrusive alert card at the top of the
// student's home page when one or more of their batches have been
// restricted by an admin. A single "Contact Help Desk" toggle reveals
// the admin-configured contact details (email, phone, WhatsApp, hours,
// instructions) so students can immediately reach out for restoration.
// ────────────────────────────────────────────────────────────────
function BatchRestrictionBanner({ restrictions, helpDesk }) {
  const [showHelp, setShowHelp] = useState(false);
  const count = restrictions.length;
  const first = restrictions[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className="mb-6 rounded-[18px] overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)",
        border: "1.5px solid #fecaca",
        boxShadow: "0 14px 34px -18px rgba(220, 38, 38, 0.35)",
      }}
      data-testid="dashboard-batch-restriction-banner"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "rgba(220, 38, 38, 0.14)", border: "1.5px solid rgba(220, 38, 38, 0.45)" }}
          >
            <Lock size={16} style={{ color: "#b91c1c" }} strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] mb-0.5"
                 style={{ color: "#b91c1c" }}>
              Batch access restricted
            </div>
            <h2 className="text-[17px] md:text-[19px] font-black leading-tight text-slate-900">
              {count === 1
                ? <>Access to <span style={{ color: "#b91c1c" }}>{first.batch_name}</span> is currently restricted</>
                : <>{count} of your batches are currently restricted</>}
            </h2>
            <p className="text-[12.5px] leading-relaxed text-slate-700 mt-1.5">
              Your other batches stay fully accessible. Contact the LW Help Desk to restore access.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {restrictions.map((r) => (
            <Link
              key={r.batch_id}
              to={`/student/study?batch=${encodeURIComponent(r.batch_id)}`}
              className="inline-flex items-center gap-1.5 pl-2 pr-3 h-7 rounded-full text-[11px] font-bold transition"
              style={{ background: "#ffffff", color: "#b91c1c", border: "1px solid #fca5a5" }}
              title={r.reason || "Restricted"}
              data-testid={`banner-restricted-chip-${r.batch_id}`}
            >
              <Lock size={9} strokeWidth={2.6} />
              <span className="truncate" style={{ maxWidth: 180 }}>{r.batch_name}</span>
            </Link>
          ))}
        </div>

        {first?.reason && (
          <div
            className="mb-3 p-3 rounded-[10px] text-[12px] leading-relaxed"
            style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#7c2d12" }}
            data-testid="banner-restricted-reason"
          >
            <span className="font-bold" style={{ color: "#9a3412" }}>Reason: </span>
            {first.reason}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className="inline-flex items-center gap-2 h-11 pl-4 pr-3 rounded-[12px] text-[13px] font-black transition"
            style={{
              background: showHelp
                ? "linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)"
                : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              color: "#ffffff",
              boxShadow: showHelp
                ? "0 6px 14px -6px rgba(30, 64, 175, 0.55)"
                : "0 10px 22px -8px rgba(37, 99, 235, 0.70)",
            }}
            data-testid="banner-help-desk-toggle"
            aria-expanded={showHelp}
          >
            <LifeBuoy size={15} strokeWidth={2.6} />
            <span>{showHelp ? "Hide contact details" : "Contact Help Desk"}</span>
            <span
              className="inline-flex items-center justify-center rounded-full"
              style={{
                width: 22, height: 22,
                background: "rgba(255,255,255,0.20)",
                marginLeft: 2,
              }}
              aria-hidden
            >
              <ChevronDown
                size={13}
                strokeWidth={3}
                style={{
                  transition: "transform 240ms cubic-bezier(0.22, 1, 0.36, 1)",
                  transform: showHelp ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </span>
          </button>
          {!showHelp && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold"
              style={{ color: "#b91c1c" }}
              data-testid="banner-help-desk-hint"
            >
              <ChevronDown size={11} strokeWidth={3} className="animate-bounce-slow" />
              Tap to see email, phone &amp; WhatsApp
            </span>
          )}
          {count === 1 && (
            <Link
              to={`/student/study?batch=${encodeURIComponent(first.batch_id)}`}
              className="inline-flex items-center gap-1.5 h-11 px-3.5 rounded-[12px] text-[12.5px] font-bold transition ml-auto"
              style={{ background: "#ffffff", color: "#0f172a", border: "1px solid #cbd5e1" }}
              data-testid="banner-view-batch"
            >
              View details <ChevronRight size={13} />
            </Link>
          )}
        </div>

        {showHelp && helpDesk && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2"
            data-testid="banner-help-desk-details"
          >
            {helpDesk.email && (
              <a
                href={`mailto:${helpDesk.email}?subject=${encodeURIComponent(`Batch access restricted — ${first.batch_name}`)}`}
                className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition"
                style={{ background: "#ffffff", border: "1px solid #cbd5e1" }}
              >
                <Mail size={14} className="shrink-0" style={{ color: "#0f172a" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500 leading-none mb-0.5">Email</div>
                  <div className="text-[12px] font-semibold text-slate-900 truncate">{helpDesk.email}</div>
                </div>
              </a>
            )}
            {helpDesk.phone && (
              <a
                href={`tel:${helpDesk.phone}`}
                className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition"
                style={{ background: "#ffffff", border: "1px solid #cbd5e1" }}
              >
                <Phone size={14} className="shrink-0" style={{ color: "#0f172a" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500 leading-none mb-0.5">Phone</div>
                  <div className="text-[12px] font-semibold text-slate-900 truncate">{helpDesk.phone}</div>
                </div>
              </a>
            )}
            {helpDesk.whatsapp && (
              <a
                href={`https://wa.me/${(helpDesk.whatsapp || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi, my access to "${first.batch_name}" has been restricted. Please help me restore access.`)}`}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-2.5 h-11 px-3.5 rounded-[10px] transition sm:col-span-2"
                style={{ background: "#dcfce7", border: "1px solid #86efac" }}
              >
                <MessageCircle size={14} className="shrink-0" style={{ color: "#16a34a" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[9.5px] uppercase tracking-wider font-bold text-emerald-700 leading-none mb-0.5">WhatsApp — fastest response</div>
                  <div className="text-[12px] font-semibold text-slate-900 truncate">{helpDesk.whatsapp}</div>
                </div>
              </a>
            )}
            {(helpDesk.hours || helpDesk.instructions) && (
              <div className="sm:col-span-2 p-3 rounded-[10px] text-[11.5px] leading-relaxed text-slate-700"
                   style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                {helpDesk.hours && <div className="mb-1"><b>Hours:</b> {helpDesk.hours}</div>}
                {helpDesk.instructions && <div>{helpDesk.instructions}</div>}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

